"use client";

import CourseCard from "./CourseCard";
import coursedetails from "@/app/premium-courses/courdetails";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaGraduationCap, FaLaptopCode, FaUserTie } from "react-icons/fa";

const AllCourses = () => {
  const headerRef = useRef(null);
  const cardsRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Header Animation
    gsap.fromTo(
      headerRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 85%",
        },
      }
    );

    // Feature Cards Stagger Animation
    const featureCards = featuresRef.current?.children;
    if (featureCards) {
      gsap.fromTo(
        featureCards,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: featuresRef.current,
            start: "top 80%",
          },
        }
      );
    }
  }, []);

  return (
    <section className="relative w-full py-20 bg-slate-50 overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-80 h-80 bg-purple-100 rounded-full blur-3xl opacity-60"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Header Section */}
        <div ref={headerRef} className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block py-1 px-3 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-4 border border-blue-100">
            Premium Learning Experience
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
            Elevate Your Career with <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Industry-Ready Courses
            </span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Master in-demand skills with our comprehensive curriculum designed by experts. 
            Get hands-on experience and personalized mentorship to fast-track your success.
          </p>
        </div>

        {/* Features Grid (Why Choose Us) */}
        <div ref={featuresRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-300">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mb-4 text-xl">
              <FaGraduationCap />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Expert Mentors</h3>
            <p className="text-slate-600 text-sm">Learn directly from industry veterans who have walked the path you want to take.</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-300">
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 mb-4 text-xl">
              <FaLaptopCode />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Live Projects</h3>
            <p className="text-slate-600 text-sm">Build a portfolio that speaks for itself with real-world capstone projects.</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-300">
            <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 mb-4 text-xl">
              <FaUserTie />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Career Support</h3>
            <p className="text-slate-600 text-sm">From resume building to mock interviews, we guide you until you're hired.</p>
          </div>
        </div>

        {/* Courses Grid */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {coursedetails.map((course, i) => (
            course.discountedPrice !== 0 && (
              <CourseCard
                key={i}
                {...course}
                index={i} // Pass index for staggered animation inside card if needed
              />
            )
          ))}
        </div>
      </div>
    </section>
  );
};

export default AllCourses;